import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Card,
  Typography,
  Space,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Title } = Typography;

const CurrenciesPage = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const queryClient = useQueryClient();

  // Fetch currencies
  const { data: currencies, isLoading } = useQuery(
    'admin-currencies',
    async () => {
      const response = await apiClient.get('/api/admin/currencies');
      return response.data.data.currencies;
    }
  );

  // Create currency mutation
  const createCurrencyMutation = useMutation(
    async (currencyData) => {
      const response = await apiClient.post('/api/admin/currencies', currencyData);
      return response.data.currencies;
    },
    {
      onSuccess: () => {
        message.success('Currency created successfully');
        queryClient.invalidateQueries('admin-currencies');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create currency');
      },
    }
  );

  // Update currency mutation
  const updateCurrencyMutation = useMutation(
    async ({ code, ...currencyData }) => {
      const response = await apiClient.put(`/api/admin/currencies/${code}`, currencyData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Currency updated successfully');
        queryClient.invalidateQueries('admin-currencies');
        setIsModalVisible(false);
        setEditingCurrency(null);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update currency');
      },
    }
  );

  // Delete currency mutation
  const deleteCurrencyMutation = useMutation(
    async (code) => {
      const response = await apiClient.delete(`/api/admin/currencies/${code}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Currency deleted successfully');
        queryClient.invalidateQueries('admin-currencies');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete currency');
      },
    }
  );

  const handleCreate = () => {
    setEditingCurrency(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (currency) => {
    setEditingCurrency(currency);
    form.setFieldsValue(currency);
    setIsModalVisible(true);
  };

  const handleDelete = (code) => {
    deleteCurrencyMutation.mutate(code);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingCurrency) {
        updateCurrencyMutation.mutate({ code: editingCurrency.code, ...values });
      } else {
        createCurrencyMutation.mutate(values);
      }
    } catch (error) {
      // Silently handle form validation error
    }
  };

  const columns = [
    {
      title: 'Currency Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Tag color="blue" icon={<DollarOutlined />}>{code}</Tag>,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this currency?"
            onConfirm={() => handleDelete(record.code)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0 }}>Currencies Management</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Currency
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={currencies}
          rowKey="code"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} currencies`,
          }}
        />
      </Card>

      <Modal
        title={editingCurrency ? 'Edit Currency' : 'Add Currency'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCurrency(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
        confirmLoading={createCurrencyMutation.isLoading || updateCurrencyMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="code"
            label="Currency Code (ISO 4217)"
            rules={[
              { required: true, message: 'Please enter currency code' },
              { len: 3, message: 'Currency code must be exactly 3 characters' },
              { pattern: /^[A-Z]+$/, message: 'Currency code must be uppercase letters only' }
            ]}
          >
            <Input
              placeholder="USD"
              maxLength={3}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CurrenciesPage;
